import React from 'react';

import { useIntl } from 'react-intl';
import { queryClient } from 'src/lib/react-query';
import ConfirmationModal from 'src/registry/components/blocks/ConfirmationModal';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { generalErrorMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';
import { toast } from 'src/hooks/useToast';
import { FinancialDocumentRow } from 'src/features/financialDocumentRows/utils/types';
import { useDeleteFinancialDocumentRow } from 'src/features/financialDocumentRows/api/useDeleteFinancialDocumentRow';
import { LIST_FINANCIAL_DOCUMENT_ROWS_RESOURCE } from 'src/features/financialDocumentRows/api/useListFinancialDocumentRows';
import { financialDocumentRowMessages } from 'src/features/financialDocumentRows/utils/messages';
import { FINANCIAL_DOCUMENT_RESOURCE } from 'src/features/financialDocuments/api/useGetFinancialDocument';

interface DeleteFinancialDocumentRowFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedDocumentRow: FinancialDocumentRow;
    children?: React.ReactNode;
}

function DeleteFinancialDocumentRowFormModal<TData>({
    selectedDocumentRow,
    children,
}: DeleteFinancialDocumentRowFormModalProps<TData>) {
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const { formatMessage } = useIntl();

    const { mutate: deleteDocumentRow } = useDeleteFinancialDocumentRow({
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [LIST_FINANCIAL_DOCUMENT_ROWS_RESOURCE] });
            void queryClient.invalidateQueries({ queryKey: [FINANCIAL_DOCUMENT_RESOURCE] });
            setIsModalOpen(false);
            toast({
                title: formatMessage(financialDocumentRowMessages.deleteFinancialDocumentRow),
                description: formatMessage(financialDocumentRowMessages.deleteFinancialDocumentRowSuccessDescription),
            });
        },
        onError: (error) => {
            if (error?.response?.status !== 400) return;
            const errorDetails = error.response?.data?.errors[0]?.detail;
            const errorCode = formatMessage(generalErrorMessages.somethingWentWrong);
            const errorMessage = errorDetails || formatMessage(generalErrorMessages.unknownError);

            toast({
                title: errorCode,
                description: errorMessage,
                variant: 'destructive',
            });
        },
    });

    return (
        <ConfirmationModal
            isModalOpen={isModalOpen}
            setIsModalOpen={setIsModalOpen}
            dialogDetails={{
                title: { ...financialDocumentRowMessages.deleteFinancialDocumentRow },
                subTitle: {
                    ...financialDocumentRowMessages.deleteFinancialDocumentRowSubtitle,
                    values: {
                        entity: selectedDocumentRow.name,
                    },
                },
                icon: ICON_MAP.DELETE,
                confirmationMessage: { ...financialDocumentRowMessages.confirmDeleteFinancialDocumentRow },
            }}
            callbackButton={{
                variant: 'destructiveLight',
                buttonTitle: financialDocumentRowMessages.deleteFinancialDocumentRow,
                callbackFn: () => {
                    return (
                        deleteDocumentRow({
                            rowId: selectedDocumentRow.id,
                        }),
                        setIsModalOpen(false)
                    );
                },
            }}
            children={children}
        />
    );
}

export default DeleteFinancialDocumentRowFormModal;
